// ─────────────────────────────────────────────────────────────────
// Glass-i — Fabrication Master page (Stage 6B.8A-3M).
//
// COMPACT INLINE CRUD — no drawer, no modal. fab_ref_name / fabRefName
// is intentionally absent from every cell, header, dropdown, and
// payload (see fabricationHelpers for the contract).
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button, Checkbox, Input, InputNumber, Select, Space, Spin, Table, Tooltip, message
} from 'antd';
import {
  SaveOutlined, ReloadOutlined,
  ToolOutlined, SearchOutlined
} from '@ant-design/icons';

import { fabricationApi } from './fabricationApi';
import {
  normalizeFabricationRows, sortFabrications,
  validateFabricationRows, buildFabricationBulkPayload, markDirtyRow
} from './fabricationHelpers';
import './FabricationMasterPage.css';

const RupeeIcon = () => (
  <span className="fab-rupee" aria-hidden="true">₹</span>
);

// Data-driven fabrication UOM (migration 0165). Fabrication bills per-piece, so
// the list is count-oriented; 'Nos' is the default. Mirrors backend FAB_UOMS.
const FAB_UOM_OPTIONS = ['Nos', 'PCS', 'SET', 'Pair', 'RFT', 'RMT', 'SQF', 'SQM']
  .map((u) => ({ value: u, label: u }));

export default function FabricationMasterPage() {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [search,  setSearch]  = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const out = await fabricationApi.list({ limit: 1000 });
      setRows(sortFabrications(normalizeFabricationRows((out && out.rows) || [])));
    } catch (e) {
      message.error(e?.response?.data?.error?.message
        || e.message || 'Failed to load fabrications.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const needle = (search || '').trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      [r.fabName, r.abbreviation, r.remarks, String(r.fabId)]
        .some((v) => v && String(v).toLowerCase().includes(needle)));
  }, [rows, search]);

  const dirtyCount = useMemo(
    () => rows.filter((r) => r._dirty).length, [rows]);

  const patch = (clientRowId, p) =>
    setRows((prev) => markDirtyRow(prev, clientRowId, p));

  // NOTE: Add Row removed per stage 6B.8A-3M.1. The Fabrication
  // Master is seeded with 15 stable fabrications and is intended to
  // be edited in place rather than extended from the UI. New rows
  // can still be added via the backend API if needed.

  const saveAll = async () => {
    const dirty = rows.filter((r) => r._dirty);
    if (dirty.length === 0) {
      message.info('Nothing to save.');
      return;
    }
    const { ok, errors } = validateFabricationRows(rows);
    if (!ok) {
      message.error(errors[0].message);
      return;
    }
    setSaving(true);
    try {
      const payload = buildFabricationBulkPayload(rows, []);
      const out = await fabricationApi.bulkSave(payload);
      message.success(
        `Saved ${out.inserted || 0} new · updated ${out.updated || 0}.`);
      load();
    } catch (e) {
      message.error(e?.response?.data?.error?.message
        || 'Failed to save fabrications.');
    } finally {
      setSaving(false);
    }
  };

  // ── Columns — Active + Actions removed per stage 6B.8A-3M.1.
  //    fab_ref_name remains NEVER shown as a column / cell.        ──
  const columns = [
    { title: 'Fab ID', dataIndex: 'fabId', width: 84, align: 'right',
      render: (v, row) => (
        <InputNumber size="small" min={1} step={1}
          value={v}
          variant="borderless" controls={false}
          style={{ width: '100%', textAlign: 'right' }}
          onChange={(n) => patch(row._clientRowId, {
            fabId: n == null ? null : Math.trunc(Number(n)) })} />
      ) },
    { title: 'Fabrication Name', dataIndex: 'fabName', width: 300,
      render: (v, row) => (
        <Input size="small" variant="borderless"
          value={v} placeholder="e.g., Holes (H)"
          maxLength={160}
          onChange={(e) => patch(row._clientRowId,
            { fabName: e.target.value })} />
      ) },
    { title: 'Abbr', dataIndex: 'abbreviation', width: 110,
      render: (v, row) => (
        <Input size="small" variant="borderless"
          value={v} placeholder="e.g., H"
          maxLength={12}
          onChange={(e) => patch(row._clientRowId,
            { abbreviation: e.target.value })} />
      ) },
    { title: 'UOM', dataIndex: 'uom', width: 96, align: 'center',
      render: (v, row) => (
        <Select size="small" variant="borderless"
          value={v || 'Nos'} options={FAB_UOM_OPTIONS}
          style={{ width: '100%' }}
          popupMatchSelectWidth={false}
          onChange={(u) => patch(row._clientRowId, { uom: u })} />
      ) },
    { title: <span>Default <RupeeIcon /></span>, dataIndex: 'defaultPrice',
      width: 130, align: 'right',
      render: (v, row) => (
        <InputNumber size="small" min={0} step={0.5} precision={2}
          value={v == null ? 0 : v}
          variant="borderless" controls={false}
          style={{ width: '100%', textAlign: 'right' }}
          onChange={(n) => patch(row._clientRowId,
            { defaultPrice: n == null ? 0 : Math.max(0, Number(n)) })} />
      ) },
    { title: 'Remarks', dataIndex: 'remarks',
      render: (v, row) => (
        <Input size="small" variant="borderless"
          value={v} placeholder="Optional notes"
          maxLength={2000}
          onChange={(e) => patch(row._clientRowId,
            { remarks: e.target.value })} />
      ) },
    { title: 'Active', dataIndex: 'isActive', width: 76, align: 'center',
      render: (v, row) => (
        <Checkbox checked={v !== false}
          onChange={(e) => patch(row._clientRowId, { isActive: e.target.checked })} />
      ) }
  ];

  return (
    <div className="fab-shell">
      <div className="fab-headstrip">
        <div className="fab-headstrip__lead">
          <span className="fab-headstrip__icon"><ToolOutlined /></span>
          <div className="fab-headstrip__text">
            <span className="fab-headstrip__title">Fabrication Master</span>
            <span className="fab-headstrip__subtitle">
              Edit any cell directly — click Save All Changes to persist.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="fab-stat-chip"><strong>{rows.length}</strong> total</span>
          {dirtyCount > 0 && (
            <span className="fab-stat-chip fab-stat-chip--dirty">
              <strong>{dirtyCount}</strong> unsaved
            </span>
          )}
        </Space>
      </div>

      <div className="fab-toolbar">
        <Input allowClear size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search fabrication name, remarks…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          style={{ width: 280 }} />
        <div className="fab-toolbar__spacer" />
        <Tooltip title="Refresh"><Button size="small"
          icon={<ReloadOutlined />} onClick={load} /></Tooltip>
        <Button size="small" type="primary" icon={<SaveOutlined />}
          onClick={saveAll} loading={saving}
          disabled={saving || dirtyCount === 0}>
          Save All Changes
        </Button>
      </div>

      <div className="fab-card">
        <Spin spinning={loading}>
          <Table className="fab-grid" size="small"
            rowKey="_clientRowId"
            dataSource={visible}
            columns={columns}
            pagination={false}
            sticky
            scroll={{ x: 'max-content' }}
            rowClassName={(r) => r._dirty ? 'fab-dirty-row' : ''}
            locale={{
              emptyText: rows.length === 0
                ? 'No fabrications loaded.'
                : 'No fabrications match the current search.'
            }} />
        </Spin>
      </div>
    </div>
  );
}
